// PolicyDocumentPage.tsx — the reader for one policy document.
//
// Comprehension is a safety feature here, not polish: a member who cannot
// understand the rules cannot follow them. Three things carry that weight —
//
//   * every section shows its plain-language summary BESIDE the binding text,
//     not instead of it;
//   * a table of contents with real anchors, so a rule can be linked to and
//     quoted rather than paraphrased;
//   * a draft banner that is impossible to miss, because none of this has been
//     through legal review.
//
// Long-form legal text is exactly where the Accessibility prefs matter most, so
// the structure is plain semantic HTML: one h1, h2 per section, a nav landmark
// for the contents, and anchors that are real links.
import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, Callout, MetaRow } from '@/ui';
import { getPolicy, POLICY_DOCUMENTS } from '@/core/data/policies';
import type { PolicySection } from '@/core/models/policy';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 76ch;
`;

const Banner = styled.p<{ $tone: 'draft' | 'critical' | 'warning' | 'info' }>`
  margin: 0;
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.55;
  border: 1px solid
    ${({ $tone, theme }) =>
      $tone === 'critical' || $tone === 'draft' ? 'transparent' : theme.color.glassBorder};
  background: ${({ $tone, theme }) =>
    $tone === 'critical' || $tone === 'draft'
      ? `color-mix(in srgb, ${theme.color.safety} 14%, transparent)`
      : theme.color.glassBg};
  color: ${({ $tone, theme }) =>
    $tone === 'critical' || $tone === 'draft' ? theme.color.safety : theme.color.textMuted};
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.display};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Meta = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Contents = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContentsTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const ContentsList = styled.ol`
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  a {
    color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  scroll-margin-top: 24px;
`;

const Heading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.color.textPrimary};
`;

/** The plain-language line. Visually distinct so it never reads as the rule itself. */
const Summary = styled.p`
  margin: 0;
  padding: 10px 14px;
  background: color-mix(in srgb, ${({ theme }) => theme.color.accentCyan} 12%, transparent);
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const SummaryLabel = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  display: block;
  margin-bottom: 4px;
`;

const Body = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};
`;

const BackLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.accentCyan};
`;

function SectionBlock({ section }: { section: PolicySection }) {
  return (
    <Section id={section.id}>
      <Heading>{section.heading}</Heading>
      <Summary>
        <SummaryLabel>In short</SummaryLabel>
        {section.summary}
      </Summary>
      {section.callout && <Banner $tone={section.callout.tone}>{section.callout.text}</Banner>}
      {section.body.map((para, i) => (
        <Body key={i}>{para}</Body>
      ))}
    </Section>
  );
}

export function PolicyDocumentPage() {
  const { docId } = useParams<{ docId: string }>();
  const doc = docId ? getPolicy(docId) : undefined;

  // Deep links to a section (/policies/terms#your-account) must land on it.
  useEffect(() => {
    if (!doc) return;
    const hash = window.location.hash.replace('#', '');
    if (hash) document.getElementById(hash)?.scrollIntoView();
  }, [doc]);

  if (!doc) return <Navigate to="/policies" replace />;

  return (
    <Page>
      <BackLink to="/policies">← All policies</BackLink>
      <Title>{doc.title}</Title>
      <Meta>
        <MetaRow>
          <span>Version {doc.version}</span>
          <span>Updated {doc.updated}</span>
        </MetaRow>
      </Meta>

      {doc.status === 'draft' && (
        <Banner $tone="draft" role="note" data-testid="policy-draft-banner">
          <strong>Draft: not yet legally reviewed.</strong> This document describes how
          TruXSocial is intended to work and is published so you can read it and tell us where
          it is wrong. It has not been checked by a lawyer, and some details are still marked
          for completion. Do not rely on it as a final legal agreement.
        </Banner>
      )}

      <Panel as={Contents} aria-label={`Contents of ${doc.title}`}>
        <ContentsTitle>Contents</ContentsTitle>
        <ContentsList>
          {doc.sections.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`}>{s.heading}</a>
            </li>
          ))}
        </ContentsList>
      </Panel>

      {/* These documents are the one part of the product a signed-out visitor is
          actually linked to (the sign-up form points at /policies/terms), and they
          are where the no-account reporting route is promised. The link lives here
          so the promise is reachable from the page that makes it. Informational
          tone: it renders on every document, Privacy included, where a danger
          border would be claiming something the page does not say. */}
      <Callout data-testid="policy-legal-floor-callout">
        <strong>Content that is unlawful to host</strong> can be reported without an account, and
        you do not need to be the person affected:{' '}
        <Link to="/report-illegal">report illegal content</Link>.
      </Callout>

      {doc.sections.map((s) => (
        <SectionBlock key={s.id} section={s} />
      ))}

      <Meta>
        The other documents:{' '}
        <MetaRow>
          {POLICY_DOCUMENTS.filter((d) => d.id !== doc.id).map((d) => (
            <Link key={d.id} to={`/policies/${d.id}`}>
              {d.title}
            </Link>
          ))}
        </MetaRow>
      </Meta>
    </Page>
  );
}
